import type { EmployeeParams } from './employee'
import { GetSingleProjectResponse } from '@/lib/types'
import { baseApi } from '@/lib/utils'

export interface TeamMember {
  employee_id: number
  hours: number
}

export interface Milestone {
  name: string
  is_completed: boolean
  start_date?: string
  end_date?: string
}
export interface Project {
  project_id: number
  list_id: number
  list_name: string
  folder_name: string
  status: string
  project_type: string
  cost: number
  contract_link?: string
  total_hours: number
  assignee_username: string
  account_manager?: string | null
  delivery_head?: string | null
  qa_status?: string | null
  assignee_name?: string | null
  archived: number
  space_name: string
  setup_completed: boolean
  start_date: string
  due_date: string
  task_count: number
  content: string
  main_archived?: boolean
  teams?: TeamMember[]
  milestones?: Milestone[]
  client_id?: string
  client_name?: string
  client_link?: string | null
  // ClickUp task id (projects_schedule.id) — required by /clients writes,
  // not the same value as project_id.
  project_task_id?: string | null
  remaining_hours?: number
  total_budget?: number
  nonbillable_duration_seconds?: number
  billable_duration_seconds?: number
  project_billable_amount?: string | number | null
  billable_revenue?: string | number | null
  billable_profit?: string | number | null
  gross_profit?: string | number | null
  net_profit?: string | number | null
  total_employee_cost?: string | number | null
  overhead_cost?: string | number | null
  fully_loaded_cost?: string | number | null
  url?: string
  clickup_list_url?: string
  list_date_created?: string | null
  wbs?: string | null
  why_doc?: string | null
  links?: ProjectLink[]
  has_vendor_work?: boolean
  vendor_id?: number | null
  vendor_name?: string | null
  vendor_payment_type?: 'full' | 'milestone' | null
}

export interface ProjectHealth {
  project_id: number
  list_id: number
  list_name: string
  project_type: string
  total_tasks: number
  completed_tasks: string | number
  total_hours: string | number
  total_budget: string | number
  billable_duration_seconds: string | number
  nonbillable_duration_seconds: string | number
  start_date: string | null
  due_date: string | null
  date_done: string | null
  profit: string | number
  billable_revenue: string | number
  total_employee_cost?: string | number
  schedule_status?: string
  budget_type?: string
}

export interface HealthResponse {
  data: ProjectHealth[]
  pagination: {
    total: number
    page: number
    limit: number
    totalPages: number
  }
}

export interface HealthParams {
  page?: number
  limit?: number
  search?: string
  startDate?: string
  endDate?: string
  projectType?: string
  scheduleStatus?: string
  missingDate?: boolean | string
  sort_by?: string
  order?: 'ASC' | 'DESC'
}

export type ProjectLinkType =
  'clickup' | 'schedule' | 'contract' | 'wbs' | 'why_doc'

export interface ProjectLink {
  type: ProjectLinkType | string
  label: string
  url: string
}

export interface ProjectsResponse {
  data: Project[]
  projectsSetupPendingCount: number
  pagination: {
    totalRecords: number
    totalPages: number
    currentPage: number
    limit: number
  }
}

export interface ProjectDetails {
  id?: number | undefined
  project_id?: number | undefined
  total_budget: number | string
  total_hours: number
  description?: string | undefined
  hourly_cost?: number | undefined
  start_date: string | null
  end_date: string | null
  created_at?: string | undefined
  milestone_id?: string[] | undefined
  milestones?: { id: string; name: string }[]
}

export interface ProjectDetailsPaginatedResponse {
  data: ProjectDetails[]
  pagination: {
    totalRecords: number
    totalPages: number
    currentPage: number
    limit: number
  }
}

export interface TeamMemberWithId extends TeamMember {
  id?: number
}

export interface MilestoneWithId extends Milestone {
  id?: number
  milestone_name?: string
}

export interface ProjectDetailsResponse {
  data: {
    project_details?: ProjectDetails[]
    teams?: TeamMemberWithId[]
    milestones?: MilestoneWithId[]
  }
}

export interface EmployeeListItem {
  user_id: number
  user_name: string
}

export interface EmployeeListResponse {
  data: EmployeeListItem[]
}

export interface ProjectTeamMembersParams {
  search?: string
  department?: string[]
}

export interface UpdateProjectMetaRequest {
  id: number
  data: {
    assignee_username?: string | undefined
    project_type?: string | undefined
    archived?: boolean | undefined
    setup_completed?: boolean | undefined
    main_archived?: boolean | undefined
    client_id?: string | null | undefined
    has_vendor_work?: boolean | undefined
    vendor_id?: number | null | undefined
    vendor_payment_type?: 'full' | 'milestone' | null | undefined
  }
}

export interface ProjectDetailsRequest {
  project_id: number
  data: ProjectDetails
}

export interface ProjectScopeData {
  teams: TeamMemberWithId[]
  milestones: MilestoneWithId[]
}

export interface UpdateProjectScopeRequest {
  project_id: number
  data: ProjectScopeData
}

export interface ApiSuccessResponse {
  message: string
  data?: unknown
}
export interface SelectProject {
  // ClickUp task id (projects_schedule.id) — /projects/select-options only
  // returns projects that have one (inner join), aliased as project_id.
  project_id: string
  list_name: string
}

export interface SelectProjectsResponse {
  success: boolean
  data: SelectProject[]
}

export interface SelectSheetProject {
  project_id: number
  list_name: string
  list_id: number
  project_type?: string
  assignee_username?: string
  client_id?: number
  client_name?: string
  setup_completed?: boolean
}

export interface BulkProjectDetailsItem {
  project_id: number
  project_type: string
  assignee_username: string
  client: { value: number | string; label: string } | null
  total_budget: string | number
  total_hours: number
  hourly_cost: number
  start_date: string | null
  end_date: string | null
  description: string
  setup_completed: boolean
}

export interface SelectSheetProjectsResponse {
  success: boolean
  data: SelectSheetProject[]
}

export interface Space {
  space_name: string
}

export interface AllSpacesResponse {
  data: Space[]
}

export interface AccountManagersResponse {
  success?: boolean
  data: string[]
}

export interface DeliveryHeadsResponse {
  success?: boolean
  data: string[]
}

export interface ProjectTl {
  id: number | string | null
  username: string
}

export interface ProjectTlsResponse {
  success?: boolean
  data: ProjectTl[]
}

export interface ProjectScheduleData {
  status: string
  start_date: string | null
  due_date: string | null
  date_done: string | null
  assignee_name: string
  priority: string
  contract_link: string
  communication: string
  billing_type: string
  account_manager: string
  wbs: string | null
  why_doc: string
  delivery_head: string | null
  total_billed_hours: number | string | null
  team: string | null
  qa_status: string | null
  department: string | null
  tech_tasks: string[]
  progress: string
  project_type: string
}

export interface ProjectScheduleResponse {
  success: boolean
  data: ProjectScheduleData
}

export interface UpdateProjectScheduleDatesPayload {
  list_id: string | number
  start_date?: string | null
  due_date?: string | null
}

export interface ProjectEmployeePerformance {
  project_id: number
  user_name: string
  total_duration_seconds: string | number
  billable_duration_seconds: string | number
  non_billable_duration_seconds: string | number
  total_billable_employee_cost: string | number
  total_billable_project_cost: string | number
  calculated_non_billable_cost: string | number
  total_employee_cost: string | number
  total_project_cost: string | number
  milestone_time_estimate_seconds?: string | number
  overhead_cost?: string | number
  fully_loaded_cost?: string | number
  clickup_id: string
}

export interface ProjectEmployeePerformanceResponse {
  success: boolean
  count: number
  data: ProjectEmployeePerformance[]
}

export interface SyncMilestone {
  id: string
  name: string
}

export interface SyncMilestonesResponse {
  success: boolean
  data: SyncMilestone[]
}

export interface ProjectTagUser {
  user_id: number
  user_name: string
  total_hours: number
  billable_hours: number
  non_billable_hours: number
  total_cost?: number
}

export interface ProjectTag {
  tag: string
  total_hours: number
  billable_hours: number
  non_billable_hours: number
  pct_of_total: number
  total_cost?: number
  users: ProjectTagUser[]
}

export interface ProjectTagsResponse {
  success: boolean
  data: {
    list_id: number
    project_id: number
    project_name: string
    estimated_hours: number
    total_hours: number
    over_under_pct: number | null
    tags: ProjectTag[]
  }
}

export interface AdminNoteEntry {
  id: number
  note: string
  created_by: number
  created_by_name: string
  created_by_email: string
  created_at: string
}

export interface AdminNotesResponse {
  success: boolean
  data: {
    project_id: number
    current: AdminNoteEntry | null
    history: AdminNoteEntry[]
  }
}

export interface SaveAdminNoteResponse {
  success: boolean
  id?: number
  unchanged?: boolean
}

export interface ProjectNoteEntry {
  id: number
  content: Record<string, unknown>
  created_by: number
  created_by_name: string
  created_by_email: string
  created_at: string
}

export interface ProjectNotesResponse {
  success: boolean
  data: {
    project_id: number
    current: ProjectNoteEntry | null
    history: ProjectNoteEntry[]
  }
}

export interface SaveProjectNoteResponse {
  success: boolean
  message: string
  data: { id: number; unchanged: boolean }
}

export interface ProjectStringsResponse {
  data: string[]
}

export interface ProjectFolderGroup {
  name: string
  subfolders: string[]
}

export interface ProjectFoldersResponse {
  data: ProjectFolderGroup[]
}

export const projectApi = baseApi.injectEndpoints({
  endpoints: builder => ({
    getProjects: builder.query<ProjectsResponse, EmployeeParams>({
      query: ({
        page = 1,
        limit = 10,
        search,
        client,
        team_id,
        account_manager,
        delivery_head,
        project_tl,
        space_name,
        status,
        datatype,
        startDate,
        endDate,
        activity,
        sort_by,
        order,
        project_status,
        folder_name,
        department,
        team_member,
      }) => ({
        url: '/projects',
        params: {
          page,
          limit,
          ...(search ? { search } : {}),
          ...(client ? { client } : {}),
          ...(team_id !== undefined ? { team_id } : {}),
          ...(account_manager ? { account_manager } : {}),
          ...(delivery_head ? { delivery_head } : {}),
          ...(project_tl ? { project_tl } : {}),
          ...(space_name ? { space_name } : {}),
          ...(status ? { status } : {}),
          ...(datatype ? { datatype } : {}),
          ...(startDate ? { startDate } : {}),
          ...(endDate ? { endDate } : {}),
          ...(activity ? { activity } : {}),
          ...(sort_by ? { sort_by } : {}),
          ...(order ? { order } : {}),
          ...(project_status?.length ? { project_status } : {}),
          ...(folder_name?.length ? { folder_name } : {}),
          ...(department?.length ? { department } : {}),
          ...(team_member?.length ? { team_member } : {}),
        },
      }),
      providesTags: ['Projects', 'ProjectDetails', 'ProjectPage'],
    }),
    getProjectSetups: builder.query<ProjectsResponse, EmployeeParams>({
      query: ({
        page = 1,
        limit = 10,
        search = '',
        client = '',
        team_id,
        account_manager,
        delivery_head,
        project_tl,
        status,
        space_name,
        datatype,
        startDate,
        endDate,
        activity,
        sort_by,
        order,
        project_status,
        folder_name,
        department,
      }) => ({
        url: '/projects/setup',
        params: {
          page,
          limit,
          search,
          client,
          ...(team_id !== undefined ? { team_id } : {}),
          ...(account_manager ? { account_manager } : {}),
          ...(delivery_head ? { delivery_head } : {}),
          ...(project_tl ? { project_tl } : {}),
          status,
          space_name,
          datatype,
          startDate,
          endDate,
          ...(activity ? { activity } : {}),
          ...(sort_by ? { sort_by } : {}),
          ...(order ? { order } : {}),
          ...(project_status?.length ? { project_status } : {}),
          ...(folder_name?.length ? { folder_name } : {}),
          ...(department?.length ? { department } : {}),
        },
      }),
      providesTags: ['Projects', 'ProjectPage'],
    }),
    getDetails: builder.query<ProjectDetailsResponse, { project_id: number }>({
      query: ({ project_id }) => `/projects/details/${project_id}`,
      providesTags: (_, _e, { project_id }) => [
        { type: 'ProjectDetails', id: project_id },
        'ProjectPage',
      ],
    }),

    updateProjectMeta: builder.mutation<
      ApiSuccessResponse,
      UpdateProjectMetaRequest
    >({
      query: ({ id, data }) => ({
        url: `/projects/update/${id}`,
        method: 'PATCH',
        body: data,
      }),
      // Linking/relinking a vendor here can also auto-create its
      // vendor_payment_schedule row server-side (see updateProjectMeta),
      // so the schedule needs to refetch alongside project details.
      // 'Projects' (the list page) only renders project_type, assignee_name,
      // client_name and archived - a vendor-only save (has_vendor_work /
      // vendor_id / vendor_payment_type) changes nothing it shows, so skip
      // that tag then to avoid a pointless full-list refetch + loading
      // overlay every time the vendor payment type is toggled.
      invalidatesTags: (_, _e, { id, data }) => {
        const listAffectingKeys = [
          'project_type',
          'assignee_username',
          'archived',
          'setup_completed',
          'main_archived',
          'client_id',
        ] as const
        const affectsList = listAffectingKeys.some(key => key in data)
        return [
          { type: 'ProjectDetails', id },
          { type: 'VendorPayments', id },
          ...(affectsList ? (['Projects'] as const) : []),
        ]
      },
    }),
    createProjectDetails: builder.mutation<
      ApiSuccessResponse,
      ProjectDetailsRequest
    >({
      query: ({ project_id, data }) => ({
        url: `/projects/details/${project_id}`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (_, _e, { project_id }) => [
        { type: 'ProjectDetails', id: project_id },
        'Projects',
      ],
    }),
    selectProjects: builder.query<
      SelectProjectsResponse,
      { projecttype?: string; pending?: boolean | string } | void
    >({
      query: args => ({
        url: '/projects/select-options',
        ...(args && { params: args }),
      }),
      providesTags: ['Projects', 'ProjectPage'],
    }),
    selectSheetProjects: builder.query<
      SelectSheetProjectsResponse,
      { search?: string }
    >({
      query: search => ({
        url: '/projects/select-options/sheet',
        method: 'GET',
        params: search,
      }),
      providesTags: ['Projects', 'ProjectPage'],
    }),
    updateProjectDetails: builder.mutation<
      ApiSuccessResponse,
      ProjectDetailsRequest
    >({
      query: ({ project_id, data }) => ({
        url: `/projects/details/${project_id}`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (_, _e, { project_id }) => [
        { type: 'ProjectDetails', id: project_id },
        'Projects',
      ],
    }),
    deleteProjectMilestone: builder.mutation<
      ApiSuccessResponse,
      { project_id: number; milestone_id: number }
    >({
      query: ({ project_id, milestone_id }) => ({
        url: `/projects/${project_id}/milestones/${milestone_id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_, _e, { project_id }) => [
        { type: 'ProjectDetails', id: project_id },
      ],
    }),
    deleteProjectTeamHours: builder.mutation<
      ApiSuccessResponse,
      { project_id: number; employee_id: number }
    >({
      query: ({ project_id, employee_id }) => ({
        url: `/projects/${project_id}/team/${employee_id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_, _e, { project_id }) => [
        { type: 'ProjectDetails', id: project_id },
      ],
    }),
    deleteProjectDetails: builder.mutation<
      ApiSuccessResponse,
      { project_id: number; id: number }
    >({
      query: ({ project_id, id }) => ({
        url: `/projects/${project_id}/details/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_, _e, { project_id }) => [
        { type: 'ProjectDetails', id: project_id },
        'Projects',
      ],
    }),

    updateProjectScope: builder.mutation<
      ApiSuccessResponse,
      UpdateProjectScopeRequest
    >({
      query: ({ project_id, data }) => ({
        url: `/projects/details/${project_id}/scope`,
        method: 'PATCH',
        body: data,
      }),
      // A new milestone auto-pairs a vendor payment schedule row
      // server-side (when the project's vendor is on the 'milestone' plan),
      // so the schedule needs to refetch alongside project details.
      invalidatesTags: (_, _e, { project_id }) => [
        { type: 'ProjectDetails', id: project_id },
        { type: 'VendorPayments', id: project_id },
      ],
    }),

    getEmployeeList: builder.query<EmployeeListResponse, void>({
      query: () => '/employees/list',
      providesTags: ['Employees'],
    }),
    getAllSpaces: builder.query<AllSpacesResponse, void>({
      query: () => '/projects/spaces',
      providesTags: ['ProjectPage'],
    }),
    getAccountManagers: builder.query<
      AccountManagersResponse,
      { pending?: boolean | string } | void
    >({
      query: args => ({
        url: '/projects/account-managers',
        ...(args && { params: args }),
      }),
      providesTags: ['ProjectPage'],
    }),
    getDeliveryHeads: builder.query<
      DeliveryHeadsResponse,
      { pending?: boolean | string } | void
    >({
      query: args => ({
        url: '/projects/delivery-heads',
        ...(args && { params: args }),
      }),
      providesTags: ['ProjectPage'],
    }),
    getProjectTls: builder.query<
      ProjectTlsResponse,
      { pending?: boolean | string } | void
    >({
      query: args => ({
        url: '/projects/project-tls',
        ...(args && { params: args }),
      }),
      providesTags: ['ProjectPage'],
    }),
    getProjectDetails: builder.query<
      ProjectDetailsPaginatedResponse,
      {
        projectId: number
        page?: number
        limit?: number
        start_date?: string
        end_date?: string
        sort_by?: string | undefined
        order?: 'ASC' | 'DESC' | undefined
      }
    >({
      query: ({
        projectId,
        page = 1,
        limit = 10,
        start_date,
        end_date,
        sort_by,
        order,
      }) => ({
        url: `/projects/budget/${projectId}`,
        params: { page, limit, start_date, end_date, sort_by, order },
        method: 'GET',
      }),
      providesTags: (_result, _error, { projectId }) => [
        { type: 'ProjectDetails', id: projectId },
        'ProjectPage',
      ],
    }),
    getSingleProject: builder.query<GetSingleProjectResponse, number>({
      query: project_id => `/projects/single/${project_id}`,
      providesTags: (_result, _error, project_id) => [
        { type: 'Projects', id: project_id },
        'ProjectPage',
      ],
    }),
    bulkCreateProjectDetails: builder.mutation<
      ApiSuccessResponse,
      BulkProjectDetailsItem[]
    >({
      query: data => ({
        url: '/projects/bulk/details',
        method: 'POST',
        body: { data },
      }),
      invalidatesTags: ['Projects', 'ProjectDetails'],
    }),
    getProjectSchedule: builder.query<
      ProjectScheduleResponse,
      string | number | undefined
    >({
      query: list_id => `/projects/schedule/${list_id}`,
      providesTags: ['ProjectPage'],
      // tech_tasks (ClickUp's "Tech Stack" multi-select field) comes back
      // from the API as the raw value stored in projects_schedule —
      // JSON.stringify(values) at sync time (jobs/projectSchedule.js), so a
      // JSON string here, not an array — normalize it once so callers can
      // rely on the string[] type declared above.
      transformResponse: (response: ProjectScheduleResponse) => {
        const raw = response.data?.tech_tasks as unknown
        let techTasks: string[] = []
        if (Array.isArray(raw)) {
          techTasks = raw
        } else if (typeof raw === 'string' && raw.trim()) {
          try {
            const parsed = JSON.parse(raw)
            if (Array.isArray(parsed)) techTasks = parsed
          } catch {
            techTasks = []
          }
        }
        return {
          ...response,
          data: { ...response.data, tech_tasks: techTasks },
        }
      },
    }),
    updateProjectScheduleDates: builder.mutation<
      ProjectScheduleResponse,
      UpdateProjectScheduleDatesPayload
    >({
      query: ({ list_id, ...body }) => ({
        url: `/projects/schedule/${list_id}/dates`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['ProjectPage', 'Projects'],
    }),
    getProjectEmployeePerformance: builder.query<
      ProjectEmployeePerformanceResponse,
      | number
      | {
          id: number
          employee?: string
          startDate?: string
          endDate?: string
        }
    >({
      query: args => {
        const { id, ...params } = typeof args === 'number' ? { id: args } : args
        return { url: `/projects/employees/${id}`, params }
      },
      providesTags: (_result, _error, args) => [
        { type: 'Projects', id: typeof args === 'number' ? args : args.id },
        'ProjectPage',
      ],
    }),
    getSyncMilestones: builder.query<SyncMilestonesResponse, string | number>({
      query: list_id => `/projects/milestones/sync/${list_id}`,
      providesTags: ['ProjectPage'],
    }),
    getProjectTags: builder.query<ProjectTagsResponse, string | number>({
      query: list_id => `/projects/tags/${list_id}`,
      providesTags: ['ProjectPage'],
    }),
    getProjectAdminNotes: builder.query<AdminNotesResponse, number>({
      query: project_id => `/projects/${project_id}/admin-notes`,
      providesTags: (_result, _error, project_id) => [
        { type: 'AdminNotes', id: project_id },
        'ProjectPage',
      ],
    }),
    saveProjectAdminNote: builder.mutation<
      SaveAdminNoteResponse,
      { project_id: number; note: string }
    >({
      query: ({ project_id, note }) => ({
        url: `/projects/${project_id}/admin-notes`,
        method: 'POST',
        body: { note },
      }),
      invalidatesTags: (_result, _error, { project_id }) => [
        { type: 'AdminNotes', id: project_id },
      ],
    }),
    getProjectNotes: builder.query<ProjectNotesResponse, number>({
      query: project_id => `/projects/${project_id}/notes`,
      providesTags: (_result, _error, project_id) => [
        { type: 'ProjectNotes', id: project_id },
        'ProjectPage',
      ],
    }),
    saveProjectNote: builder.mutation<
      SaveProjectNoteResponse,
      { project_id: number; content: Record<string, unknown> }
    >({
      query: ({ project_id, content }) => ({
        url: `/projects/${project_id}/notes`,
        method: 'POST',
        body: { content },
      }),
      invalidatesTags: (_result, _error, { project_id }) => [
        { type: 'ProjectNotes', id: project_id },
      ],
    }),
    getProjectStatuses: builder.query<ProjectStringsResponse, void>({
      query: () => '/projects/statuses',
      providesTags: ['ProjectPage'],
    }),
    getProjectDepartments: builder.query<ProjectStringsResponse, void>({
      query: () => '/projects/departments',
      providesTags: ['ProjectPage'],
    }),
    getProjectTeamMembers: builder.query<
      EmployeeListResponse,
      ProjectTeamMembersParams | void
    >({
      query: ({ search, department } = {}) => ({
        url: '/projects/team-members',
        params: {
          ...(search ? { search } : {}),
          ...(department?.length ? { department } : {}),
        },
      }),
      providesTags: ['ProjectPage', 'Employees'],
    }),
    getProjectFolders: builder.query<ProjectFoldersResponse, void>({
      query: () => '/projects/folders',
      providesTags: ['ProjectPage'],
    }),
    exportProjectsCsv: builder.mutation<
      void,
      Omit<EmployeeParams, 'page' | 'limit'>
    >({
      queryFn: async (
        {
          search,
          client,
          team_id,
          account_manager,
          delivery_head,
          project_tl,
          space_name,
          status,
          datatype,
          startDate,
          endDate,
          activity,
          sort_by,
          order,
          project_status,
          folder_name,
          department,
          team_member,
        },
        _api,
        _extraOptions,
        baseQuery
      ) => {
        const result = await baseQuery({
          url: '/projects/export/csv',
          params: {
            ...(search ? { search } : {}),
            ...(client ? { client } : {}),
            ...(team_id !== undefined ? { team_id } : {}),
            ...(account_manager ? { account_manager } : {}),
            ...(delivery_head ? { delivery_head } : {}),
            ...(project_tl ? { project_tl } : {}),
            ...(space_name ? { space_name } : {}),
            ...(status ? { status } : {}),
            ...(datatype ? { datatype } : {}),
            ...(startDate ? { startDate } : {}),
            ...(endDate ? { endDate } : {}),
            ...(activity ? { activity } : {}),
            ...(sort_by ? { sort_by } : {}),
            ...(order ? { order } : {}),
            ...(project_status?.length ? { project_status } : {}),
            ...(folder_name?.length ? { folder_name } : {}),
            ...(department?.length ? { department } : {}),
            ...(team_member?.length ? { team_member } : {}),
          },
          responseHandler: response => response.blob(),
        })
        if (result.error) return { error: result.error }
        const blob = result.data as Blob
        const objectUrl = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = objectUrl
        link.setAttribute('download', 'projects.csv')
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(objectUrl)
        return { data: undefined }
      },
    }),
    getHealth: builder.query<HealthResponse, HealthParams>({
      query: ({
        page = 1,
        limit = 10,
        search,
        startDate,
        endDate,
        projectType,
        scheduleStatus,
        missingDate,
        sort_by,
        order,
      }) => ({
        url: '/health',
        params: {
          page,
          limit,
          ...(search ? { search } : {}),
          ...(startDate ? { startDate } : {}),
          ...(endDate ? { endDate } : {}),
          ...(projectType ? { projectType } : {}),
          ...(scheduleStatus ? { scheduleStatus } : {}),
          ...(missingDate !== undefined ? { missingDate } : {}),
          ...(sort_by ? { sort_by } : {}),
          ...(order ? { order } : {}),
        },
      }),
      providesTags: ['Projects'],
    }),
  }),
})

export const {
  useGetProjectsQuery,
  useGetProjectSetupsQuery,
  useGetDetailsQuery,
  useSelectProjectsQuery,
  useUpdateProjectMetaMutation,
  useCreateProjectDetailsMutation,
  useUpdateProjectDetailsMutation,
  useUpdateProjectScopeMutation,
  useDeleteProjectMilestoneMutation,
  useDeleteProjectTeamHoursMutation,
  useDeleteProjectDetailsMutation,
  useGetEmployeeListQuery,
  useGetAllSpacesQuery,
  useGetAccountManagersQuery,
  useGetDeliveryHeadsQuery,
  useGetProjectTlsQuery,
  useGetProjectDetailsQuery,
  useGetSingleProjectQuery,
  useSelectSheetProjectsQuery,
  useBulkCreateProjectDetailsMutation,
  useGetProjectScheduleQuery,
  useUpdateProjectScheduleDatesMutation,
  useGetProjectEmployeePerformanceQuery,
  useGetSyncMilestonesQuery,
  useGetProjectTagsQuery,
  useGetProjectAdminNotesQuery,
  useSaveProjectAdminNoteMutation,
  useGetProjectNotesQuery,
  useSaveProjectNoteMutation,
  useGetHealthQuery,
  useGetProjectStatusesQuery,
  useGetProjectDepartmentsQuery,
  useGetProjectTeamMembersQuery,
  useGetProjectFoldersQuery,
  useExportProjectsCsvMutation,
} = projectApi
